import React, { useEffect, useMemo, useRef, useState } from 'react'
import { trackEvent as trackIntercomEvent } from '@intercom/messenger-js-sdk'
import { intercomAppId } from 'config'
import useTracker from 'src/lib/js/hooks/useTracker'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import {
  useGuestIntercomJwtQuery,
  DiningOptionBehavior,
  GuestCurrencyInitializeRefundErrorCode,
  OrderSource
} from 'src/apollo/onlineOrdering'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { LoadingSpinner } from 'src/shared/components/common/loading_spinner/LoadingSpinner'
import { t } from 'src/translations/src'
import RefundResolutionModal from './RefundResolutionModal'
import IssueSelectionModal from './IssueSelectionModal'
import ItemSelectionModal from './ItemSelectionModal'
import { ExceptionKind, Reason } from '../lib/reasons'
import { refundReasonFor } from '../lib/refundReason'
import {
  MissingItemInput,
  refundTotalAmount,
  mapCompletedOrderSelections
} from '../lib/refundItemPricing'
import { RefundQuote, useInitializeRefund } from '../lib/useInitializeRefund'
import {
  buildIntercomData,
  buildRefundIntercomAttributes,
  toIntercomCustomAttributes,
  EXCEPTION_REFUND_METHOD,
  GuestRefundMethod,
  ManualReviewMethod,
  RefundMethod
} from '../lib/dataMapper'
import { useIntercomRefundSession } from '../lib/useIntercomRefundSession'
import { isWithinClaimWindow } from '../lib/claimWindow'
import RecoveryExceptionModal, {
  ExceptionAction
} from './RecoveryExceptionModal'

// Brand teal — matches $refund-toast-green in refund_resolution_modal.scss (no shared token).
const REFUND_BRAND_COLOR = '#007a6b'
const LOADING_SPINNER_SIZE = '48'

// Stands in for a quote never asked for (see `quotable`). Reads as settled and non-retryable so
// D3 shows its declined state instead of spinning on a request that will never be made.
const UNQUOTABLE: RefundQuote = {
  refund: null,
  errorCode: null,
  retryable: false,
  loading: false,
  settled: true,
  failed: true
}

// Intercom space holding the guest's own tickets. `showSpace` takes a free-form string, so a
// wrong name fails silently and the space must be enabled; the documented fallback is 'messages'.
const TICKET_SPACE_NAME = 'tickets'

// iOS's own identifiers, so both platforms' funnels aggregate together.
const EXCEPTION_ANALYTICS_ID: Record<ExceptionKind, string> = {
  alreadyReported: 'already_reported',
  outsideClaimWindow: 'outside_claim_window'
}

// The "Get help" recovery card. Owns the D1→D2→D3 step state and keeps a signed JWT per refund
// method ready, so choosing one boots without waiting on the network. Signatures cover the guest's
// selections, so they re-sign as those change. Lifecycle lives in useIntercomRefundSession.

// Toast Local DELIVERY order (gated on source, not the browsing surface), logged-in guest, flag on.
export function useShowIntercomGetHelp(): boolean {
  const { customer } = useCustomer()
  const { completedOrder } = useCompletedOrder()
  const { tdsRevereWebIntercom } = useFlags()
  const flagEnabled = Boolean(tdsRevereWebIntercom)
  const loggedIn = Boolean(customer?.id)
  const isToastLocalOrder = completedOrder?.source === OrderSource.Local
  // Every D1 reason describes a courier leg, which takeout/dine-in has none of. False here is also
  // what keeps the restaurant's phone number: it is OrderHelpSection's fallback.
  const isDelivery =
    completedOrder?.diningOptionBehavior === DiningOptionBehavior.Delivery
  return flagEnabled && isToastLocalOrder && isDelivery && loggedIn
}

export const IntercomGetHelpCard = () => {
  const { completedOrder } = useCompletedOrder()
  const { customer } = useCustomer()
  const tracker = useTracker()
  const ooClient = useOOClient()
  // 'exception' is terminal — entered instead of D1 (outside the window) or out of D3 (already
  // reported). 'quoting' is manual review's stand-in for D3 while its quote and JWT settle, where
  // the guest sees only the loading buffer.
  const [step, setStep] = useState<
    'closed' | 'issue' | 'items' | 'method' | 'quoting' | 'exception'
  >('closed')
  const [selectedReason, setSelectedReason] = useState<Reason | null>(null)
  const [selectedItems, setSelectedItems] = useState<MissingItemInput[]>([])
  // Set when the guest ENTERS the flow, never unset: the method signatures below are useless to
  // anyone who never presses Get help, so signing at mount cost every page view two unread
  // round-trips. Sticky, not derived from `step`, so re-entry and handoff don't re-run them.
  const [recoveryEntered, setRecoveryEntered] = useState(false)

  const intercomData = buildIntercomData(completedOrder)
  const orderGuid = completedOrder?.guid ?? ''
  const rxGuid = completedOrder?.restaurant?.guid ?? ''

  const refundableItems = useMemo(
    () => mapCompletedOrderSelections(completedOrder?.selections),
    [completedOrder?.selections]
  )

  // Pinned to the order, not to every render: the window must not close underneath a claim the
  // guest already started. Fails open when the date is missing — see lib/claimWindow.ts.
  const withinClaimWindow = useMemo(
    () => isWithinClaimWindow(completedOrder?.createdDateTime),
    [completedOrder?.createdDateTime]
  )

  const orderSupportSelection = selectedReason?.value

  const { quote: serverQuote, requestQuote, resetQuote } = useInitializeRefund()

  // The enum the mutation reserves under; null for no selection and for sub-menu parents, which
  // never reach here. With the two guids, decides whether a quote can be asked for at all.
  const refundReason = refundReasonFor(orderSupportSelection)
  const quotable = Boolean(refundReason && orderGuid && rxGuid)
  const quote = quotable ? serverQuote : UNQUOTABLE

  const refundTotalAmountValue = quote.refund
    ? refundTotalAmount(quote.refund)
    : undefined

  // All from the reserved refund or from nothing: an unquoted claim signs no amount rather than a
  // zero, and 'Refund GUID' is the reservation the agent finalizes.
  const refundSelections = {
    orderSupportSelection,
    refundGuid: quote.refund?.id,
    baseRefundAmount: quote.refund?.baseAmount.amount,
    bonusRefundAmount: quote.refund?.bonusAmount.amount,
    // Restated for the agent, who reads the conversation rather than the reservation.
    selectedItemGuids: selectedItems.map((item) => item.selectionGuid)
  }

  // iOS's routeRefundableIssue: a zero-subtotal order can't be quoted, so it falls back to manual
  // review. Post-discount deliberately — preDiscountItemsSubtotal would miss a fully-discounted
  // order, the case this guard exists for.
  const zeroSubtotal = completedOrder?.itemsSubtotal === 0

  // The fixed method a reason hands off with, bypassing D3 — reasons.ts's `method`, or the subtotal
  // guard overriding an otherwise-'refundable' reason. Undefined means the guest picks in D3. Takes
  // `reason` as an argument so the handlers can resolve it in the same tick they set the state.
  const resolveMethod = (reason: Reason): ManualReviewMethod | undefined =>
    reason.method ?? (zeroSubtotal ? RefundMethod.ManualReview : undefined)

  const manualReviewMethod = selectedReason
    ? resolveMethod(selectedReason)
    : undefined

  const toastCashAttributes = buildRefundIntercomAttributes(completedOrder, {
    selectedRefundMethod: RefundMethod.ToastCash,
    ...refundSelections
  })
  const originalPaymentAttributes = buildRefundIntercomAttributes(
    completedOrder,
    {
      selectedRefundMethod: RefundMethod.OriginalPaymentMethod,
      ...refundSelections
    }
  )
  // Signed only once a manual-review method is in play: most claims never need it, and signing for
  // all of them would add a fourth round-trip to every selection change.
  const manualReviewAttributes = manualReviewMethod
    ? buildRefundIntercomAttributes(completedOrder, {
        selectedRefundMethod: manualReviewMethod,
        ...refundSelections
      })
    : null

  // Derived, not stored, so it can't drift from the quote that produced it. Window first: it is
  // known at mount and short-circuits before any quote exists, so the two can never both be live.
  const exceptionKind: ExceptionKind | null = !withinClaimWindow
    ? 'outsideClaimWindow'
    : quote.errorCode ===
      GuestCurrencyInitializeRefundErrorCode.RefundAlreadyIssued
    ? 'alreadyReported'
    : null

  // Signed as soon as the exception is KNOWN — the CTA that needs it is one press away. It rides on
  // 'Selected refund method' (the attribute the refund audiences match) with a value none of them
  // honour, so it routes to a human; 'Order support selection' passes through unchanged. No
  // 'Refund GUID' or amounts — nothing was reserved. See README.md § Exception states.
  const exceptionAttributes = exceptionKind
    ? buildRefundIntercomAttributes(completedOrder, {
        selectedRefundMethod: EXCEPTION_REFUND_METHOD[exceptionKind],
        orderSupportSelection
      })
    : null

  // These sign the D1 reason and the amount preview, so they re-sign as selections change and
  // `data` is undefined for the new variables while in flight. Nothing below may gate on that.
  // Skipped until the guest enters the flow (`recoveryEntered`). Safe because launchRefundFlow
  // leaves the modal up and tracks `unsigned_method` on an unlanded signature — the fastest read is
  // two clicks away: a reason with no item step and no fixed method goes D1 → D3.
  const { data: toastCashJwtData, error: toastCashJwtError } =
    useGuestIntercomJwtQuery({
      client: ooClient,
      variables: {
        customAttributes: toastCashAttributes
          ? toIntercomCustomAttributes(toastCashAttributes)
          : []
      },
      skip: !customer?.id || !recoveryEntered,
      fetchPolicy: 'cache-first'
    })
  const { data: originalPaymentJwtData, error: originalPaymentJwtError } =
    useGuestIntercomJwtQuery({
      client: ooClient,
      variables: {
        customAttributes: originalPaymentAttributes
          ? toIntercomCustomAttributes(originalPaymentAttributes)
          : []
      },
      skip: !customer?.id || !recoveryEntered,
      fetchPolicy: 'cache-first'
    })

  // Skipped until an exception is in play — most orders never hit one. 'Selected refund method' is
  // verified-updates, so this JWT is the only way to set it: `update()` claims are silently dropped.
  const { data: exceptionJwtData, error: exceptionJwtError } =
    useGuestIntercomJwtQuery({
      client: ooClient,
      variables: {
        customAttributes: exceptionAttributes
          ? toIntercomCustomAttributes(exceptionAttributes)
          : []
      },
      skip: !customer?.id || !exceptionAttributes,
      fetchPolicy: 'cache-first'
    })

  // Manual review's signature — same "skip until needed" reasoning as the exception one above.
  const { data: manualReviewJwtData, error: manualReviewJwtError } =
    useGuestIntercomJwtQuery({
      client: ooClient,
      variables: {
        customAttributes: manualReviewAttributes
          ? toIntercomCustomAttributes(manualReviewAttributes)
          : []
      },
      skip: !customer?.id || !manualReviewAttributes,
      fetchPolicy: 'cache-first'
    })

  const toastCashJwt = toastCashJwtData?.guest?.intercomJWT ?? undefined
  const originalPaymentJwt =
    originalPaymentJwtData?.guest?.intercomJWT ?? undefined
  const exceptionJwt = exceptionJwtData?.guest?.intercomJWT ?? undefined
  const manualReviewJwt = manualReviewJwtData?.guest?.intercomJWT ?? undefined

  // A failed signature looks identical to "not back yet" in the UI, so only telemetry sees it.
  const jwtError =
    toastCashJwtError ??
    originalPaymentJwtError ??
    exceptionJwtError ??
    manualReviewJwtError
  useEffect(() => {
    if (!jwtError) return
    tracker.track(TrackingEvents.TDS_REFUND_SUPPORT_UNAVAILABLE, {
      context: 'order_confirmation_intercom',
      reason: 'jwt_query_error',
      order_guid: orderGuid,
      error: jwtError.message
    })
  }, [jwtError, orderGuid, tracker])

  // REFUND_ALREADY_ISSUED means a request is already in flight. D3 would render that as its generic
  // declined state, which reads as transient when there is nothing to retry — so route to the
  // terminal screen, which points the guest at the request they have. 'quoting' counts too
  // (TDS-2916): a manual-review reason never reaches 'method' but can decline the same way, and
  // would otherwise launch a second conversation over a claim an agent may already be working —
  // what TDS-2915 shipped this exception for. The launch effect below yields to this one.
  useEffect(() => {
    if (
      (step === 'method' || step === 'quoting') &&
      exceptionKind === 'alreadyReported'
    ) {
      setStep('exception')
    }
  }, [step, exceptionKind])

  // Once per entry, not per render: the card re-renders freely and `viewed` is a funnel event.
  useEffect(() => {
    if (step !== 'exception' || !exceptionKind) return
    tracker.track(TrackingEvents.TDS_REFUND_EXCEPTION_VIEWED, {
      context: 'order_confirmation_intercom',
      exception: EXCEPTION_ANALYTICS_ID[exceptionKind],
      order_guid: orderGuid
    })
  }, [step, exceptionKind, orderGuid, tracker])

  const guestName =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') ||
    undefined

  const session = useIntercomRefundSession({
    appId: intercomAppId,
    userId: customer?.id ?? undefined,
    email: customer?.email ?? undefined,
    name: guestName,
    order: orderGuid ? { guid: orderGuid, rxGuid } : null,
    toastCashJwt,
    toastCashAttributes: toastCashAttributes as Record<string, unknown> | null
  })

  // warm() pre-boots behind the page with the Toast Cash signature, now requested by the same press
  // that opens D1 — so it can't run in that tick, and fires on the render the signature lands.
  // Once per ENTRY: warm() aborts and re-boots, and the signature re-signs on every selection
  // change, so re-running would re-boot under a claim in progress. `phase` is the second guard.
  const warmedRef = useRef(false)
  useEffect(() => {
    if (warmedRef.current || !recoveryEntered || !toastCashJwt) return
    if (session.phase !== 'idle') return
    warmedRef.current = true
    session.warm()
  }, [recoveryEntered, toastCashJwt, session])

  // Boots Intercom for `method`. Defined ahead of the early return because the launch effect below
  // calls it from a hook, and hooks must run unconditionally.
  const launchRefundFlow = (method: GuestRefundMethod | ManualReviewMethod) => {
    const isManualReview =
      method === RefundMethod.ManualReview || method === RefundMethod.Other

    // No reserved refund, no handoff — except manual review, which hands off to a human whether
    // quoting succeeded or not. For the two guest-chosen methods this is a backstop: booting now
    // would sign an empty 'Refund GUID' and empty amounts, which the workflow reads as a refund of
    // nothing and dead-ends.
    if (!isManualReview && !quote.refund) {
      tracker.track(TrackingEvents.TDS_REFUND_SUPPORT_UNAVAILABLE, {
        context: 'order_confirmation_intercom',
        reason: 'no_refund_quote',
        order_guid: orderGuid,
        refund_method: method
      })
      return
    }

    // Per method, because each boots with its own JWT. session.open() only guards on
    // appId/userId/order, so without this a method whose signature failed would boot an unverified
    // session carrying no signed attributes. It must also be the CURRENT signature: it embeds the
    // reason and amount, so a stale one quotes Intercom a different refund than the guest saw.
    const methodJwt =
      method === RefundMethod.ToastCash
        ? toastCashJwt
        : method === RefundMethod.OriginalPaymentMethod
        ? originalPaymentJwt
        : manualReviewJwt
    const methodAttrs =
      method === RefundMethod.ToastCash
        ? toastCashAttributes
        : method === RefundMethod.OriginalPaymentMethod
        ? originalPaymentAttributes
        : manualReviewAttributes
    if (!methodJwt) {
      // Leave the modal open rather than closing onto nothing — a re-sign lands well inside a
      // second, so the CTA works on a second press. The effect below re-fires for manual review.
      tracker.track(TrackingEvents.TDS_REFUND_SUPPORT_UNAVAILABLE, {
        context: 'order_confirmation_intercom',
        reason: 'unsigned_method',
        order_guid: orderGuid,
        refund_method: method
      })
      return
    }

    setStep('closed')
    tracker.track(TrackingEvents.CLICKED_TDS_RX_CONTACT, {
      context: 'order_confirmation_intercom',
      restaurant_guid: completedOrder?.restaurant?.guid ?? '',
      refund_method: method
    })
    trackIntercomEvent('tds_get_help_requested', {
      order_guid: completedOrder?.guid,
      restaurant_guid: completedOrder?.restaurant?.guid,
      refund_method: method,
      order_support_selection: selectedReason?.value,
      refund_amount: refundTotalAmountValue,
      refund_item_count: selectedItems.length || undefined
    })
    session.open(
      method,
      methodJwt,
      methodAttrs as Record<string, unknown> | null
    )
  }

  // Releases everything the pre-boot applied on the way into D1, not just the step. Defined ahead
  // of the early return, unlike the other D1/D2 handlers, because the manual-review bail-out below
  // is a hook and must be able to call it unconditionally.
  const closeModal = () => {
    setStep('closed')
    resetQuote()
    session.clearActive()
  }

  // Manual review skips D3, so no guest action calls launchRefundFlow for it — this fires once the
  // quote settles. Called even before manualReviewJwt exists: launchRefundFlow's unsigned branch
  // tracks that and leaves step at 'quoting', and the jwt is a dep, so it re-fires when it lands.
  // Not exhaustive on the deps that are redefined every render — only readiness signals belong.
  useEffect(() => {
    if (step !== 'quoting' || !manualReviewMethod) return
    // The already-reported exception owns the terminal screen for this claim once it's
    // known — the effect above routes to it, but on the SAME commit this effect's own
    // `step` closure still reads 'quoting' (setStep in the effect above doesn't retroactively
    // change what THIS effect sees this pass), so without this check it would launch anyway,
    // one render before 'exception' takes effect.
    if (exceptionKind) return
    // A permanently failed signature is a dead end, not a wait: the selections are frozen
    // once the quote has settled, so the query's variables can never change to trigger a
    // retry, and manualReviewJwt would never arrive. 'quoting' has no close/back control —
    // unlike D3 or the exception screens, which stay open specifically so a re-sign in
    // flight can land — so leaving `step` there would strand the guest on the loading
    // buffer indefinitely. closeModal() (not just setStep('closed')) is required here: the
    // pre-boot effect above already called session.warm() on the way into D1, which applies
    // the 'tds-refund-support-active' backdrop class and injects the host style — without
    // releasing those too, the guest is left on a permanently dimmed page with nothing to
    // dismiss, worse than the spinner this branch replaces. D3's own declined state isn't a
    // safe fallback either: it would render guest-facing method options if the quote itself
    // had succeeded, for a reason that is manual-review-only.
    if (manualReviewJwtError) {
      tracker.track(TrackingEvents.TDS_REFUND_SUPPORT_UNAVAILABLE, {
        context: 'order_confirmation_intercom',
        reason: 'manual_review_jwt_failed',
        order_guid: orderGuid,
        refund_method: manualReviewMethod
      })
      closeModal()
      return
    }
    if (!quote.settled) return
    launchRefundFlow(manualReviewMethod)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    manualReviewMethod,
    exceptionKind,
    manualReviewJwtError,
    quote.settled,
    manualReviewJwt
  ])

  // The JWTs deliberately do NOT gate this: they re-sign on every selection change, so gating would
  // unmount the card — and the modal in use — for a round-trip. Boot readiness is per method.
  if (!intercomData || !customer?.id) return null

  // Reserves the refund for `reason`, on the way INTO D3 (or manual review) and nowhere else: the
  // mutation reserves server-side, so quoting per reason or per item tick would reserve refunds for
  // claims the guest never files. Mirrors iOS's routeRefundableIssue / routeQuotedDeliveryIssue.
  // `reason` and `items` are arguments, not state: the handlers call this in the same tick as their
  // setState, so state still holds the PREVIOUS claim and would scope the wrong selections.
  const quoteRefund = (reason: Reason, items: readonly MissingItemInput[]) => {
    const reasonEnum = refundReasonFor(reason.value)
    // Unquotable reasons render D3's declined state via UNQUOTABLE.
    if (!reasonEnum || !orderGuid || !rxGuid) return
    void requestQuote({
      orderGuid,
      rxGuid,
      reason: reasonEnum,
      selectionGuids: items.map((item) => item.selectionGuid)
    })
  }

  const handleSelectReason = (reason: Reason) => {
    setSelectedReason(reason)
    setSelectedItems([])
    if (reason.route === 'itemSelector') {
      // D2 first — the quote waits until the guest is on their way to D3.
      setStep('items')
      return
    }
    // No item step, so the claim is the whole order. A fixed method skips D3 (see the effect above).
    quoteRefund(reason, [])
    setStep(resolveMethod(reason) ? 'quoting' : 'method')
  }

  const handleSelectItems = (items: MissingItemInput[]) => {
    setSelectedItems(items)
    // `items`, not `selectedItems`: the setState above has not committed yet.
    if (selectedReason) quoteRefund(selectedReason, items)
    setStep(
      selectedReason && resolveMethod(selectedReason) ? 'quoting' : 'method'
    )
  }

  // Only reachable for INTERNAL_ERROR. Re-sends the same selections, so the scope is unchanged.
  const retryQuote = () => {
    if (selectedReason) quoteRefund(selectedReason, selectedItems)
  }

  // Resume only with a captured conversationId — the only thing that reopens the EXACT
  // conversation. Never show(): it surfaces the most-recent one and mixes across orders.
  const handleGetHelpClick = () => {
    const marker = session.readMarker(orderGuid, rxGuid)
    if (marker?.conversationId && session.hasLiveSession()) {
      session.resume(marker)
      return
    }
    // Ahead of D1 and warm(): an order past the window can't be claimed. Deliberately AFTER the
    // resume branch — a guest who filed in time keeps their conversation reachable forever.
    if (!withinClaimWindow) {
      setStep('exception')
      return
    }
    setSelectedReason(null)
    setSelectedItems([])
    // Starts the method signatures AND, once one lands, the pre-boot. Not set on the branches above:
    // neither reads a method signature (resume never re-boots, the exception CTA has its own).
    setRecoveryEntered(true)
    // Re-arm the pre-boot per entry. Here, not in closeModal: `recoveryEntered` is sticky and close
    // returns `phase` to 'idle', so clearing it there would pre-boot behind a page the guest left.
    warmedRef.current = false
    setStep('issue')
  }

  // Both terminal CTAs close the modal and hand off to Intercom — see README.md § Exception states.
  const handleExceptionAction = (action: ExceptionAction) => {
    // Unreachable while mounted, but the kind names the event and picks the attributes.
    if (!exceptionKind) return

    tracker.track(TrackingEvents.TDS_REFUND_EXCEPTION_ACTION, {
      context: 'order_confirmation_intercom',
      exception: EXCEPTION_ANALYTICS_ID[exceptionKind],
      action,
      order_guid: orderGuid
    })

    if (action === 'viewRequest') {
      // No boot, by design: booting would open a SECOND conversation over a claim already filed.
      setStep('closed')
      session.openSpace(TICKET_SPACE_NAME)
      return
    }

    // Same rule as launchRefundFlow: unsigned, the boot carries no verified 'Selected refund
    // method', so the workspace has nothing to route it by. Leave the modal up.
    if (!exceptionJwt) {
      tracker.track(TrackingEvents.TDS_REFUND_SUPPORT_UNAVAILABLE, {
        context: 'order_confirmation_intercom',
        reason: 'unsigned_exception',
        order_guid: orderGuid,
        exception: EXCEPTION_ANALYTICS_ID[exceptionKind]
      })
      return
    }

    setStep('closed')
    session.openSupport(
      exceptionJwt,
      exceptionAttributes as Record<string, unknown> | null
    )
  }

  return (
    <div className='getInTouchContainer'>
      <div className='getInTouch'>
        <div className='textContent'>
          <h3>{t('order-confirmation.behavior.get-in-touch.header')}</h3>
          <p>{t('order-confirmation.recovery.card-subtitle')}</p>
        </div>
        <button
          type='button'
          className='getHelpButton'
          data-testid='get-help-intercom-button'
          onClick={handleGetHelpClick}
        >
          {t('order-confirmation.behavior.get-in-touch.get-help')}
        </button>
      </div>
      <IssueSelectionModal
        isOpen={step === 'issue'}
        onClose={closeModal}
        onSelectReason={handleSelectReason}
      />
      <ItemSelectionModal
        isOpen={step === 'items'}
        items={refundableItems}
        onBack={() => setStep('issue')}
        onClose={closeModal}
        onSelectItems={handleSelectItems}
      />
      {/* D3 renders the quote's pending and declined states itself, so only the order is needed. */}
      {completedOrder && (
        <RefundResolutionModal
          isOpen={step === 'method'}
          onClose={closeModal}
          // Back retraces whichever way the guest came in, D1 or D2. Each step clears its own
          // selection when it reopens, so going back restarts it rather than restoring the answer.
          onBack={() => {
            // Drop the quote on the way out: the next pass through D3 asks again.
            resetQuote()
            setStep(
              selectedReason?.route === 'itemSelector' ? 'items' : 'issue'
            )
          }}
          order={completedOrder}
          quote={quote}
          onRetry={retryQuote}
          onSelectMethod={launchRefundFlow}
        />
      )}
      {/* Gated on the kind too: closing resets the quote it derives from, so this would otherwise
          flash a titleless screen on the way out. */}
      {step === 'exception' && exceptionKind && (
        <RecoveryExceptionModal
          isOpen
          kind={exceptionKind}
          orderCreatedDateTime={completedOrder?.createdDateTime}
          onClose={closeModal}
          onAction={handleExceptionAction}
        />
      )}
      {/* Loading buffer over the modal→chat handoff, and over the quote+JWT wait for a
          manual-review reason, which has no D3 to wait inside. It can hold the dimmed page for ~6s
          ('opening') or longer ('quoting'), and the spinner is a bare SVG, so the wait is announced
          for screen-reader and magnifier users. role='status' implies aria-live='polite'; both are
          spelled out because the announcement is the point. */}
      {(session.phase === 'opening' || step === 'quoting') && (
        <div
          className='refundSupportLoading'
          data-testid='refund-support-loading'
          role='status'
          aria-live='polite'
        >
          <LoadingSpinner
            color={REFUND_BRAND_COLOR}
            size={LOADING_SPINNER_SIZE}
          />
          <span className='screenReaderOnly'>
            {t('order-confirmation.recovery.opening-chat')}
          </span>
        </div>
      )}
    </div>
  )
}
